import React, { useMemo } from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { useIsDisabledByParent } from '../../../../../../v2BaseComponents/IsDisabledProvider';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import useOrganizationInvitationDefinitions from '../../../../../../../hooks/api/useOrganizationInvitationDefinitions';
import OrganizationClientInvitationFields, {
    clientRecordToFormValue,
    getDefaultFormValue,
    formValueToCreateClientRecordInvitationRequest,
    OrganizationClientInvitationDisableProvisionProfileReason,
} from '../../../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import MaybePublicOrgMissingInfoAlert from '../../../../../../shared/MaybePublicOrgMissingInfoAlert';
import { getIntl } from '../../../../../../../utils/intl';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';

const intl = getIntl('profiles.addClientRecordInvitationSideDrawerContent', {
    send: 'Send',
});

export type AddClientRecordInvitationSideDrawerContentOwnProps = {
    onClose: (force: boolean) => void;
    clientRecordId: string;
};

export type AddClientRecordInvitationSideDrawerContentProps = AddClientRecordInvitationSideDrawerContentOwnProps;

const AddClientRecordInvitationSideDrawerContent: React.FC<AddClientRecordInvitationSideDrawerContentProps> = (
    props,
) => {
    const { isLoadingClientRecord, clientRecord, isInvitingClientRecord, inviteClientRecord } =
        useOrganizationClientRecords({
            organizationClientRecordId: props.clientRecordId,
            onInviteClientRecordSuccess: () => props.onClose(false),
        });
    const { invitationDefinitions, isLoadingInvitationDefinitions } = useOrganizationInvitationDefinitions({});

    const initialValues = useMemo(
        () =>
            clientRecord?.clientDetails
                ? clientRecordToFormValue(clientRecord, invitationDefinitions)
                : getDefaultFormValue(invitationDefinitions),
        [clientRecord, invitationDefinitions],
    );

    const isLoading =
        isLoadingClientRecord || isLoadingInvitationDefinitions || isInvitingClientRecord || !clientRecord;

    const isDisabled = useIsDisabledByParent(isLoading);

    let disableProvisionProfileReason: undefined | OrganizationClientInvitationDisableProvisionProfileReason;
    if (clientRecord && clientRecordStatusTypeGuard.invitationRejected(clientRecord)) {
        disableProvisionProfileReason = 'inviting-rejected-client';
    } else if (
        clientRecord &&
        clientRecordStatusTypeGuard.notInvited(clientRecord) &&
        !!clientRecord.clientMedicalProfileId
    ) {
        disableProvisionProfileReason = 'inviting-previous-client';
    }

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={initialValues}
                        onSubmit={(values) => {
                            const createClientRecordInviteRequest =
                                formValueToCreateClientRecordInvitationRequest(values);
                            inviteClientRecord({
                                organizationClientRecordId: props.clientRecordId,
                                request: createClientRecordInviteRequest,
                            });
                        }}
                        isDisabled={isDisabled}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit, values }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isDisabled || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => props.onClose(true)}
                                    saveText={intl('send')}
                                    saveIcon="Send"
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <MaybePublicOrgMissingInfoAlert />
                                    <OrganizationClientInvitationFields
                                        invitationDefinitions={invitationDefinitions}
                                        value={values}
                                        disableProvisionProfileReason={disableProvisionProfileReason}
                                    />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddClientRecordInvitationSideDrawerContent);
